import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Upload, Wallet, ChevronDown, ListTree, FileSpreadsheet, Link2, Landmark } from "lucide-react";
import { PageHeader, UpdatedThroughChip } from "@/components/layout/PageHeader";
import {
    HeroWaterfall,
    HeadlineCards,
    BranchLeaderboard,
    ExpenseDestination,
    ProfitTrend,
    InsightsStrip,
} from "@/components/financials/FinancialsStory";
import { FinMonthlyDetailSection } from "@/components/financials/FinancialsSections";
import PeriodPicker from "@/components/sales/PeriodPicker";
import FinancialsReconciliationPanel from "@/components/financials/FinancialsReconciliationPanel";
import ImportFinancialsDialog from "@/components/financials/ImportFinancialsDialog";
import ImportErpPlDialog from "@/components/financials/ImportErpPlDialog";
import CostCenterMapDialog from "@/components/financials/CostCenterMapDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { DataScope, monthOf } from "@/components/common/DataScope";
import { Card, CardContent } from "@/components/ui/card";
import { OPS } from "@/constants/testIds";
import { lastDayOfMonth } from "@/lib/format";

// ------------------------------------------------------------ data hook
function useFin(path, params, enabled = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const clean = Object.fromEntries(
        Object.entries(params || {}).filter(([, v]) => v !== null && v !== undefined && v !== "")
    );
    const key = JSON.stringify(clean);
    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        let active = true;
        setLoading(true);
        api
            .get(path, { params: clean })
            .then((r) => active && setData(r.data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, key, enabled]);
    return { data, loading };
}

export default function FinancialsPage() {
    const { me, isSuperAdmin } = useAuth();
    const { t, tn, lang } = useI18n();
    const [branch, setBranch] = useState("all");
    const [period, setPeriod] = useState({ start: null, end: null, label: null });
    const [periodInit, setPeriodInit] = useState(false);
    const [meta, setMeta] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [erpImportOpen, setErpImportOpen] = useState(false);
    const [ccMapOpen, setCcMapOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [signal, setSignal] = useState(0);

    useEffect(() => {
        let active = true;
        api
            .get("/financials/meta")
            .then((r) => active && setMeta(r.data))
            .catch(() => active && setMeta(null));
        return () => {
            active = false;
        };
    }, [signal]);

    // Default the view to the latest month that actually HAS a P&L — the picker
    // now also offers the months still waiting on an ERP export, and opening on
    // an empty one would look broken.
    useEffect(() => {
        if (!periodInit && meta?.months?.length) {
            const withData = meta.months.filter((m) => m.has_data !== false);
            const last = (meta.latest_month_with_data
                ? meta.months.find((m) => m.value === meta.latest_month_with_data)
                : null) || withData[withData.length - 1] || meta.months[meta.months.length - 1];
            setPeriod({
                start: `${last.value}-01`,
                end: lastDayOfMonth(last.value),
                label: lang === "ar" ? last.label_ar : last.label_en,
            });
            setPeriodInit(true);
        }
    }, [meta, periodInit, lang]);

    const hasData = meta?.has_data;
    const branches = me?.financials?.branches || [];
    // Normalize range: a YYYY-MM bound (e.g. from the picker clamp against a
    // month-grain coverage_to) is expanded to a full YYYY-MM-DD so the backend
    // never sees start > end.
    const start = period.start && period.start.length === 7 ? `${period.start}-01` : period.start;
    const end = period.end && period.end.length === 7 ? lastDayOfMonth(period.end) : period.end;
    const selectedMonthYM = start && end && start.slice(0, 7) === end.slice(0, 7) ? start.slice(0, 7) : null;
    const isSingleMonth = !!selectedMonthYM;

    // Centralized fetches (passed down to presentational sections).
    const kpiQ = useFin("/financials/kpi", { branch, start, end }, !!hasData);
    const expQ = useFin("/financials/expenses", { branch, start, end }, !!hasData);
    const brQ = useFin("/financials/branches", { start, end }, !!hasData);
    const moQ = useFin("/financials/monthly", { branch }, !!hasData); // all months (trend + MoM)

    const onSelectBranch = (id) => {
        setBranch(id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <DataScope month={monthOf(end)}>
        <div data-testid={OPS.financials.page} className="space-y-6">
            <PageHeader
                icon={Landmark}
                title={t("financials_title")}
                subtitle={t("financials_subtitle")}
                meta={
                    meta?.coverage_from ? (
                        <UpdatedThroughChip
                            label={t("coverage")}
                            value={`${meta.coverage_from} → ${meta.coverage_to}`}
                        />
                    ) : null
                }
                actions={
                    <>
                        {hasData ? (
                            <div data-testid={OPS.financials.periodPicker}>
                                <PeriodPicker meta={meta} value={period} onChange={setPeriod} />
                            </div>
                        ) : null}
                        {hasData ? (
                            <Select value={branch} onValueChange={setBranch}>
                                <SelectTrigger data-testid={OPS.financials.branchFilter} className="lg:w-[200px]">
                                    <SelectValue placeholder={t("branch_filter")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t("all_branches_fin")}</SelectItem>
                                    {branches.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            {tn(b)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : null}
                        {isSuperAdmin ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        data-testid={OPS.financials.dataMenu}
                                        variant="outline"
                                        className="gap-2"
                                    >
                                        <Upload className="h-4 w-4" />
                                        {t("import_financials")}
                                        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem
                                        data-testid={OPS.financials.importBtn}
                                        onClick={() => setImportOpen(true)}
                                        className="cursor-pointer gap-2"
                                    >
                                        <Upload className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                        {t("import_financials")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid={OPS.erpPl.importBtn}
                                        onClick={() => setErpImportOpen(true)}
                                        className="cursor-pointer gap-2"
                                    >
                                        <FileSpreadsheet className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                        {t("erp_pl_import")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid={OPS.ccMap.openBtn}
                                        onClick={() => setCcMapOpen(true)}
                                        className="cursor-pointer gap-2"
                                    >
                                        <Link2 className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                        {t("cc_map_btn")}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : null}
                    </>
                }
            />

            {isSuperAdmin ? <FinancialsReconciliationPanel signal={signal} /> : null}

            {!hasData ? (
                <Card data-testid={OPS.financials.emptyState} className="surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)]">
                    <CardContent className="py-4">
                        <EmptyState icon={Wallet} title={t("financials_empty")} hint={t("financials_empty_hint")} dataset="financials_pl_monthly" />
                    </CardContent>
                </Card>
            ) : (
                <div data-testid={OPS.financials.story} className="fin-scope space-y-5 sm:space-y-6">
                    {/* 1. Hero money-flow */}
                    <HeroWaterfall kpi={kpiQ.data} expenses={expQ.data} loading={kpiQ.loading || expQ.loading} isSingleMonth={isSingleMonth} />

                    {/* 2. Plain-language headline cards */}
                    <HeadlineCards
                        kpi={kpiQ.data}
                        monthlySeries={moQ.data}
                        selectedMonthYM={selectedMonthYM}
                        isSingleMonth={isSingleMonth}
                        loading={kpiQ.loading}
                    />

                    {/* 6. Quick insights (placed high for the 30-second read) */}
                    <InsightsStrip
                        kpi={kpiQ.data}
                        expenses={expQ.data}
                        branches={brQ.data}
                        monthlySeries={moQ.data}
                        isSingleMonth={isSingleMonth}
                        loading={kpiQ.loading || expQ.loading || brQ.loading || moQ.loading}
                    />

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
                        {/* 3. Branch leaderboard */}
                        <div className="lg:col-span-7">
                            <BranchLeaderboard branches={brQ.data} selectedBranchId={branch} onSelectBranch={onSelectBranch} loading={brQ.loading} />
                        </div>
                        {/* 4. Where the money goes */}
                        <div className="lg:col-span-5">
                            <ExpenseDestination expenses={expQ.data} loading={expQ.loading} />
                        </div>
                    </div>

                    {/* 5. Profit trend */}
                    <ProfitTrend monthlySeries={moQ.data} loading={moQ.loading} />

                    {/* 7. Full detail behind an expander */}
                    <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
                        <CollapsibleTrigger asChild>
                            <Button
                                data-testid={OPS.financials.detailsExpander}
                                variant="outline"
                                className="w-full justify-center gap-2 surface border-[color:var(--border-soft)] text-[color:var(--ink)] hover:bg-[color:rgba(186,217,235,0.18)]"
                            >
                                <ListTree className="h-4 w-4 text-[color:var(--brand-brown)]" />
                                {detailsOpen ? t("fin_hide_details") : t("fin_see_details")}
                                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${detailsOpen ? "rotate-180" : ""}`} />
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-4">
                            <FinMonthlyDetailSection branch={branch} meta={meta} />
                        </CollapsibleContent>
                    </Collapsible>
                </div>
            )}

            {isSuperAdmin ? (
                <ImportFinancialsDialog open={importOpen} onOpenChange={setImportOpen} onIngested={() => setSignal((s) => s + 1)} />
            ) : null}
            {isSuperAdmin ? (
                <ImportErpPlDialog open={erpImportOpen} onOpenChange={setErpImportOpen} onIngested={() => setSignal((s) => s + 1)} />
            ) : null}
            {isSuperAdmin ? <CostCenterMapDialog open={ccMapOpen} onOpenChange={setCcMapOpen} /> : null}
        </div>
        </DataScope>
    );
}
